import React from 'react'
import PropTypes from 'prop-types'
import { isEmpty } from 'ramda'

import { useDispatch } from 'react-redux'
import { updateFragmentValue } from 'actions/powerTakeOff'

import { useSelector } from 'hooks/redux'
import { getGroupBoundaries } from 'selectors/powerTakeOff'

import { extendSchema, yup } from 'schemas/validation'

import strings from 'utils/strings'

import { Header } from 'components/patterns'
import { FragmentWarningIconStyled } from 'components/patterns/Icon/IconsStyled'

import Boundaries from './Boundaries'

import DynamicInput from '../DynamicInput'
import {
  StyledInputsWrapper,
  StyledParagraph,
  StyledDrawerDivider,
  StyledWarningMessage,
} from './GroupEditor.style'

const GroupEditor = ({
  fragments,
  enables,
  enabledBy,
  lowerBound,
  upperBound,
  updateValue,
  templateId,
  disabled,
  readOnly,
}) => {
  const dispatch = useDispatch()

  const groupBoundaries = useSelector(getGroupBoundaries, {
    lowerBound,
    upperBound,
    fragments,
    templateId,
  })

  const {
    upperBoundMin,
    lowerBoundMax,
    upperBoundFragments,
    lowerBoundFragments,
  } = groupBoundaries

  const showBoundaries = upperBoundMin || lowerBoundMax

  const renderFragments = (refIds) => {
    const inputs = refIds.map((refNo) => {
      const {
        name,
        configuration: {
          units,
          value,
          type,
          definedValues,
          translations,
          lowerBound: fragmentLowerBound,
          upperBound: fragmentUpperBound,
          originalValue,
        } = {},
        isEcfFragmentOutOfRange,
        description,
        moduleNo,
      } = fragments[refNo] || {}

      const valueIsOriginalValue = originalValue === value

      const handleChange = (newValue) => dispatch(updateValue(refNo, newValue))

      const isValueFragment = fragmentLowerBound || fragmentUpperBound

      // if the fragment has bounds, then it is the 'value' fragment
      // and we must validate it is in the correct boundaries
      // investigate why using the 'schemaProp' pattern doesn't work here
      // *** Per task 42157 Turning off boundary validations until better design ***
      const rules = isValueFragment
        ? extendSchema(
            yup.mixed().includedIn('definedValues'),
            // .lessThanContext('upperBoundMin')
            // .moreThanContext('lowerBoundMax'),
            {
              definedValues,
              // lowerBoundMax,
              // upperBoundMin,
            }
          )
        : extendSchema(yup.mixed().includedIn('definedValues'), {
            definedValues,
          })

      return (
        <DynamicInput
          disabled={isEmpty(definedValues) || disabled}
          refNo={refNo}
          key={refNo}
          name={name}
          units={units}
          value={value}
          description={description}
          moduleNo={moduleNo}
          type={type}
          definedValues={definedValues}
          hideClosestValues={isEcfFragmentOutOfRange && valueIsOriginalValue}
          translations={translations}
          onChange={handleChange}
          makeLabel={() => (units ? `${name} ${units}` : name)}
          rules={
            isEcfFragmentOutOfRange && valueIsOriginalValue
              ? extendSchema(yup.mixed())
              : rules
          }
          readOnly={readOnly}
        >
          {isEcfFragmentOutOfRange && valueIsOriginalValue && (
            <StyledWarningMessage>
              <div>
                <FragmentWarningIconStyled />
              </div>
              <span>
                {strings.pto_default_fragment_value_warning(
                  originalValue,
                  units
                )}
              </span>
            </StyledWarningMessage>
          )}
        </DynamicInput>
      )
    })

    return <StyledInputsWrapper>{inputs}</StyledInputsWrapper>
  }

  if (!fragments) return null

  return (
    <>
      <StyledDrawerDivider />

      <Header type="h3">{strings.enables}</Header>
      {renderFragments(enabledBy)}

      <StyledDrawerDivider />

      <Header type="h3">{strings.settings}</Header>
      {renderFragments(enables)}

      {showBoundaries && (
        <>
          <StyledParagraph>
            {strings.fragment_value_explanation}
          </StyledParagraph>
          <Boundaries
            lowerBoundFragments={lowerBoundFragments}
            upperBoundFragments={upperBoundFragments}
          />
        </>
      )}
    </>
  )
}

GroupEditor.defaultProps = {
  updateValue: updateFragmentValue,
}

GroupEditor.propTypes = {
  groupId: PropTypes.string,
  fragments: PropTypes.object,
  enables: PropTypes.array,
  enabledBy: PropTypes.array,
  lowerBound: PropTypes.array,
  upperBound: PropTypes.array,
  updateValue: PropTypes.func,
  templateId: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
}

export default GroupEditor
